import {
  UICorePlugin,
  type SupportedVersion,
  type AttributeMap,
  Playback,
  $
} from '@clappr/core'
import {Events} from 'common/types/events'
import type {Options} from 'common/types/options'
import type {Poster} from 'common/plugins/poster'
import type {Advertisement} from 'common/plugins/advertisement'
import type {Viewport} from 'common/plugins/viewport'
import './styles.scss'

interface Size {
  width: number
  height: number
}

// use runtime symbols to prevent replacing keys by extensions
const ON_VISIBLE_AREA_CHANGED: unique symbol = Symbol('onVisibleAreaChanged')

const ASPECT_RATIO_TRESHOLD = 0.01

export class VideoBackdrop<T extends Options> extends UICorePlugin<Options> {
  private requestId?: number
  private isVisible?: boolean
  private currentSize!: Size
  private videoCanvas?: HTMLCanvasElement
  private advertisementCanvas?: HTMLCanvasElement

  public get name(): string {
    return 'videoBackdrop'
  }

  public get supportedVersion(): SupportedVersion {
    return {min: '0.5.0'}
  }

  public get attributes(): AttributeMap {
    return {
      'data-video-backdrop': ''
    }
  }

  private get poster(): Poster<T> | null {
    return this.core.getPlugin('poster') as Poster<T> | null
  }

  private get viewport(): Viewport<T> | null {
    return this.core.getPlugin('viewport') as Viewport<T> | null
  }

  private get advertisement(): Advertisement<T> | null {
    return this.core.getPlugin('advertisement') as Advertisement<T> | null
  }

  private get shouldRender(): boolean {
    return (
      this.core.options.videoBackdropColor !== 'none' &&
      (this.core.activePlayback?.getPlaybackType() !== Playback.NO_OP ||
        !!this.core.options.handledLiveErrorCode)
    )
  }

  public bindEvents(): void {
    this.listenTo(this.core, Events.CORE_READY, this.onReady)
    this.listenTo(this.core, Events.CORE_RESIZE, this.onResize)
    this.listenTo(
      this.core,
      Events.CORE_ACTIVE_CONTAINER_CHANGED,
      this.onActiveContainerChange
    )

    const {activeContainer} = this.core

    if (activeContainer) {
      this.listenTo(activeContainer, Events.CONTAINER_PLAY, this.onPlay)
      this.listenTo(activeContainer, Events.CONTAINER_PAUSE, this.onPause)
      this.listenTo(activeContainer, Events.CONTAINER_STOP, this.onEnded)
      this.listenTo(activeContainer, Events.CONTAINER_ENDED, this.onEnded)
    }

    const {poster, viewport, advertisement} = this

    if (poster) {
      this.listenTo(poster, Events.POSTER_SHOW, this.renderPoster)
      this.listenTo(poster, Events.POSTER_HIDE, this.render)
    }

    if (viewport) {
      this.listenTo(
        viewport,
        Events.VIEWPORT_VISIBLE_AREA_CHANGED,
        this[ON_VISIBLE_AREA_CHANGED]
      )
    }

    if (advertisement) {
      this.listenTo(
        advertisement,
        Events.ADVERTISEMENT_STARTED,
        this.onAdStarted
      )
      this.listenTo(advertisement, Events.ADVERTISEMENT_RESUMED, this.onPlay)
      this.listenTo(advertisement, Events.ADVERTISEMENT_PAUSED, this.onPause)
      this.listenTo(advertisement, Events.ADVERTISEMENT_COMPLETE, this.onPause)
      this.listenTo(advertisement, Events.ADVERTISEMENT_ENDED, this.onAdEnded)
      this.listenTo(advertisement, Events.ADVERTISEMENT_ERROR, this.onAdError)
    }
  }

  public stopListening(): this {
    this.cancelAnimation()

    return super.stopListening()
  }

  private onActiveContainerChange(): void {
    this.stopListening()
    this.bindEvents()
  }

  private onReady(): void {
    this.render()
    this.onResize()
  }

  private onResize(): void {
    this.currentSize = {
      width: this.el.offsetWidth,
      height: this.el.offsetHeight
    }
  }

  // eslint-disable-next-line sonar/function-name
  private [ON_VISIBLE_AREA_CHANGED](visibleHeight: number): void {
    this.isVisible = visibleHeight > 0
  }

  private onPlay(): void {
    if (!this.requestId) {
      this.startAnimation()
    }
  }

  private onPause(): void {
    this.cancelAnimation()
  }

  private onEnded(): void {
    // NOTE: clear context when video ends
    this.cleanCanvas(this.videoCanvas)
    this.cancelAnimation()
  }

  private onAdStarted(): void {
    this.render()
    this.toggleCanvas(true)
    this.onPlay()
  }

  private onAdEnded(): void {
    this.toggleCanvas(false)
    // NOTE: clear context when ad ends
    this.cleanCanvas(this.advertisementCanvas)
    this.cancelAnimation()
  }

  private onAdError(): void {
    if (this.advertisement?.isPlaying()) {
      this.onAdEnded()
    }
  }

  private toggleCanvas(hasActiveAd: boolean): void {
    $(this.videoCanvas).toggle(!hasActiveAd)
    $(this.advertisementCanvas).toggle(hasActiveAd)
  }

  private cleanCanvas(canvas?: HTMLCanvasElement): void {
    if (canvas) {
      canvas.width = this.currentSize.width
    }
  }

  private startAnimation = (): void => {
    this.renderBackdrop()
    this.requestId = window.requestAnimationFrame(this.startAnimation)
  }

  private cancelAnimation(): void {
    if (this.requestId) {
      window.cancelAnimationFrame(this.requestId)
      delete this.requestId
    }
  }

  private renderBackdrop(force = false): void {
    const {advertisement} = this
    const {activePlayback} = this.core
    // NOTE: try to find external ad video element if ad is playing
    const videoElement =
      (advertisement?.isPlaying() &&
        [].slice
          .call(advertisement?.el.querySelectorAll('video'))
          .find(
            (element: HTMLVideoElement) => !element.paused && !element.ended
          )) ||
      (activePlayback.el as HTMLVideoElement)
    const shouldRender = force || (!videoElement.paused && !videoElement.ended)

    if (!videoElement || !shouldRender) {
      this.cancelAnimation()

      return
    }

    // NOTE: render video backdrop only if player in viewport and not buffering
    if (
      this.isVisible &&
      (videoElement !== activePlayback.el || !activePlayback.buffering)
    ) {
      this.renderElement(
        videoElement,
        videoElement.videoWidth,
        videoElement.videoHeight
      )
    }
  }

  private renderPoster(): void {
    const {activePlayback} = this.core
    const videoElement = activePlayback.el as HTMLVideoElement

    if (
      activePlayback.tagName === 'video' &&
      videoElement &&
      !videoElement.paused &&
      !videoElement.ended
    ) {
      return
    }

    if (this.shouldRender) {
      activePlayback?.$el.after(this.el)
    }

    const {poster} = this.core.options
    let imageUrl

    if (typeof poster === 'string') {
      imageUrl = poster
    } else if (poster?.custom) {
      imageUrl = poster.custom.replace(/(^.*url\(["']?|["']?\).*$)/gi, '')
    } else {
      imageUrl = poster?.url
    }

    if (!imageUrl) {
      return
    }

    const imageElement = new Image()

    imageElement.addEventListener('load', () =>
      this.renderElement(imageElement, imageElement.width, imageElement.height)
    )
    imageElement.src = imageUrl
  }

  private renderElement(
    element: HTMLVideoElement | HTMLImageElement,
    contentWidth: number,
    contentHeight: number
  ): void {
    const canvas = this.advertisement?.isPlaying()
      ? this.advertisementCanvas
      : this.videoCanvas
    const context = canvas?.getContext('2d')

    if (!canvas || !context) {
      return
    }

    const {width, height} = this.currentSize
    const aspectRatio = width / height
    const contentAspectRatio = contentWidth / contentHeight

    // NOTE: don't stream content to backdrop if video and player have equal sizes
    if (Math.abs(aspectRatio - contentAspectRatio) < ASPECT_RATIO_TRESHOLD) {
      return
    }

    const scaledWidth = Math.ceil(
      contentAspectRatio > aspectRatio ? height * contentAspectRatio : width
    )
    const scaledHeight = Math.ceil(
      contentAspectRatio > aspectRatio ? height : width / contentAspectRatio
    )
    const offsetX = Math.ceil((scaledWidth - width) / 2)
    const offsetY = Math.ceil((scaledHeight - height) / 2)

    canvas.width = width
    canvas.height = height

    // NOTE: safari doesn't support canvas filter
    // https://developer.mozilla.org/en-US/docs/Web/API/CanvasRenderingContext2D/filter#browser_compatibility
    if ('filter' in CanvasRenderingContext2D.prototype) {
      context.filter = 'blur(30px)'
    } else {
      canvas.style.filter = 'blur(30px)'
    }

    context.drawImage(element, -offsetX, -offsetY, scaledWidth, scaledHeight)
  }

  public render(): this {
    if (this.shouldRender) {
      const videoBackdropColor = this.core.options.videoBackdropColor

      if (videoBackdropColor) {
        this.el.style.backgroundColor = videoBackdropColor
      } else {
        const isInitialRender = !this.videoCanvas

        this.videoCanvas ??= document.createElement('canvas')
        this.advertisementCanvas ??= document.createElement('canvas')

        if (isInitialRender) {
          this.$el.append(this.videoCanvas)
          this.$el.append(this.advertisementCanvas)
          this.toggleCanvas(false)
        }
      }

      this.core.activePlayback?.$el.before(this.el)
    }

    return this
  }
}
